/**
 * Reusable Form Validation Utility Functions
 * Used across CapitalOfficeForm, RevenueForm, and other forms in the application.
 */

// 1. Email format regex
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// 2. URL format regex (http, https, or domain with TLD)
export const URL_REGEX = /^(https?:\/\/)?([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?$/i;

// 3. International / Local Phone Regex
export const PHONE_REGEX = /^\+?[\d\s\-()]{7,20}$/;

// 4. Numerical Amount Regex (supports $, USD, commas, decimals, and suffixes like k, m, b, cr, l, lakh, crore, million, billion, mn, bn)
export const NUMERIC_AMOUNT_REGEX = /^(\$|USD\s*|INR\s*|Rs\.?\s*|₹\s*|£\s*|€\s*)?\s*(\d{1,3}(,\d{3})*|\d+)(\.\d+)?\s*(k|m|b|cr|l|lakh|crore|million|billion|mn|bn)?$/i;

// 5. Percentage Regex (supports optional +/- and optional %)
export const PERCENTAGE_REGEX = /^[+-]?\s*(\d+(\.\d+)?)\s*%?$/;

// 6. Whole Integer / Count Regex (supports commas and optional k/m suffixes)
export const INTEGER_REGEX = /^\s*(\d{1,3}(,\d{3})*|\d+)\s*(k|m)?\s*$/i;

/**
 * Validate required text string with minimum character length
 */
export const validateRequired = (value, fieldName = 'This field', minLength = 1) => {
    if (value === undefined || value === null || String(value).trim() === '') {
        return `${fieldName} is required.`;
    }
    const trimmed = String(value).trim();
    if (trimmed.length < minLength) {
        return `${fieldName} must be at least ${minLength} characters.`;
    }
    return null;
};

/**
 * Validate email address
 */
export const validateEmail = (email, fieldName = 'Work email', isRequired = true) => {
    if (!email || !String(email).trim()) {
        return isRequired ? `${fieldName} is required.` : null;
    }
    const trimmed = String(email).trim();
    if (!EMAIL_REGEX.test(trimmed)) {
        return `${fieldName} is not in a valid format (e.g. you@company.com). Please change it to continue.`;
    }
    return null;
};

/**
 * Validate Website or Resource URL
 */
export const validateUrl = (url, fieldName = 'Website URL', isRequired = false) => {
    if (!url || !String(url).trim()) {
        return isRequired ? `${fieldName} is required.` : null;
    }
    const trimmed = String(url).trim();
    if (!URL_REGEX.test(trimmed)) {
        return `${fieldName} is not in a valid URL format (e.g. https://company.com or company.com). Please change it to continue.`;
    }
    return null;
};

/**
 * Validate Phone Number
 */
export const validatePhone = (phone, fieldName = 'Phone number', isRequired = false) => {
    if (!phone || !String(phone).trim()) {
        return isRequired ? `${fieldName} is required.` : null;
    }
    const trimmed = String(phone).trim();
    const digits = trimmed.replace(/\D/g, '');
    if (!PHONE_REGEX.test(trimmed) || digits.length < 7 || digits.length > 15) {
        return `${fieldName} is not in a valid format (e.g. +91 98765 43210). Please change it to continue.`;
    }
    return null;
};

/**
 * Validate Numerical / Financial Currency Amount
 */
export const validateNumeric = (val, fieldName = 'Amount', isRequired = false) => {
    if (val === undefined || val === null || String(val).trim() === '') {
        return isRequired ? `${fieldName} is required.` : null;
    }
    const str = String(val).trim();
    if (!NUMERIC_AMOUNT_REGEX.test(str)) {
        return `${fieldName} must be a valid numerical amount (e.g. 2,500,000 or 2.5M). Please change it to continue.`;
    }
    return null;
};

/**
 * Validate Percentage within a realistic range
 * Default realistic max set to 1000% (e.g. 10x growth) instead of unrealistic 10000%.
 */
export const validatePercentage = (val, fieldName = 'Percentage', min = 0, max = 100, isRequired = false) => {
    if (val === undefined || val === null || String(val).trim() === '') {
        return isRequired ? `${fieldName} is required.` : null;
    }
    const str = String(val).trim();
    const match = str.match(PERCENTAGE_REGEX);
    if (!match) {
        return `${fieldName} must be a valid percentage (e.g. 14% or 25). Please change it to continue.`;
    }
    const num = parseFloat(match[1]);
    if (min !== null && num < min) {
        return `${fieldName} cannot be less than ${min}%. Please change it to continue.`;
    }
    if (max !== null && num > max) {
        return `${fieldName} cannot exceed ${max}% (entered ${num}%). Please enter a realistic value (e.g. 25% or 150%).`;
    }
    return null;
};

/**
 * Validate Whole Number / Count
 */
export const validateInteger = (val, fieldName = 'Count', isRequired = false) => {
    if (val === undefined || val === null || String(val).trim() === '') {
        return isRequired ? `${fieldName} is required.` : null;
    }
    const str = String(val).trim();
    if (!INTEGER_REGEX.test(str)) {
        return `${fieldName} must be a valid whole number (e.g. 150 or 5k). Please change it to continue.`;
    }
    return null;
};

/**
 * Validate Target Close Date (MM/YYYY, Month YYYY, Q1-Q4 YYYY)
 */
export const validateDate = (val, fieldName = 'Target close date', isRequired = true, minYear = 2020, maxYear = 2099) => {
    if (!val || !String(val).trim()) {
        return isRequired ? `${fieldName} is required.` : null;
    }
    const trimmed = String(val).trim();
    const mmYyyyRegex = /^(\d{1,2})\s*[\/\-]\s*(\d{2,4})$/;
    const yyyyMmRegex = /^(\d{4})\s*[\/\-]\s*(\d{1,2})$/;
    const textMonthRegex = /^(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:tember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)|q[1-4])\s*[\/\-,\s]\s*(\d{2,4})$/i;

    let month = null;
    let year = null;

    const mmYyyyMatch = trimmed.match(mmYyyyRegex);
    const yyyyMmMatch = trimmed.match(yyyyMmRegex);
    const textMonthMatch = trimmed.match(textMonthRegex);

    if (mmYyyyMatch) {
        month = parseInt(mmYyyyMatch[1], 10);
        let y = parseInt(mmYyyyMatch[2], 10);
        if (y < 100) y += 2000;
        year = y;
        if (month < 1 || month > 12) {
            return `${fieldName} month must be between 01 and 12 (e.g. 03/2027). Please change it to continue.`;
        }
    } else if (yyyyMmMatch) {
        year = parseInt(yyyyMmMatch[1], 10);
        month = parseInt(yyyyMmMatch[2], 10);
        if (month < 1 || month > 12) {
            return `${fieldName} month must be between 01 and 12 (e.g. 03/2027). Please change it to continue.`;
        }
    } else if (textMonthMatch) {
        let y = parseInt(textMonthMatch[2], 10);
        if (y < 100) y += 2000;
        year = y;
    } else {
        return `${fieldName} is not in a valid format. Please specify in MM / YYYY format (e.g. 03/2027 or March 2027) to continue.`;
    }

    if (year < minYear || year > maxYear) {
        return `${fieldName} year must be a valid 4-digit year (${minYear}-${maxYear}). Please change it to continue.`;
    }
    return null;
};

/**
 * Validate Select / Dropdown choice
 */
export const validateSelect = (val, fieldName = 'Selection', isRequired = true) => {
    if (isRequired && (!val || !String(val).trim())) {
        return `Please select ${fieldName.toLowerCase().startsWith('a ') || fieldName.toLowerCase().startsWith('an ') ? fieldName : `a ${fieldName}`}.`;
    }
    return null;
};

/**
 * Validate Checkbox / Boolean Declaration
 */
export const validateBoolean = (val, errorMessage) => {
    if (!val) {
        return errorMessage;
    }
    return null;
};
